import { useState } from 'react';
import type { ReactNode } from 'react';
import type { Eu } from '../../acesso';
import { lerEventoDaTrilha, lerSaude, lerTrilha } from '../../administracao';
import type { EstadoDoServico, EventoDaTrilha, SaudeDoSistema } from '../../administracao';
import { EstadoVazio, ErroDeArea, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  IconeLocal,
  SeloDoAmbiente,
  diaHora,
  diaISO,
  hojeISO,
  hora,
  nomeDoAmbiente,
  useLeitura,
  useVersao,
} from './sistema-comum';
import type { Versao } from './sistema-comum';
import './saude.css';

/**
 * TELA 36 — SAÚDE, AUDITORIA, SEGURANÇA, DADOS E SOBRE.
 *
 *   "Uma central operacional do InovaPay — não um SOC."
 *
 * - Os estados vêm do servidor. Serviço sem verificação fica "Desconhecido", com o motivo.
 * - Incidente só aparece se houver serviço degradado ou indisponível, e diz o impacto — sem
 *   inventar número de incidente (o InovaPay não abre incidentes).
 * - A trilha é SÓ CONSULTA: filtros por pessoa, área e dia, 50 por página; o detalhe mostra os
 *   metadados já redigidos pelo servidor.
 * - Bloqueio de qualidade ≠ conta bloqueada: dois números, dois links.
 * - Backup: o servidor não recebe o estado — a tela diz isso, nunca "Sucesso".
 * - Segundo fator não existe: "não disponível".
 */

type Aba = 'visao' | 'auditoria' | 'seguranca' | 'dados' | 'sobre';
const ABAS: readonly { aba: Aba; rotulo: string }[] = [
  { aba: 'visao', rotulo: 'Visão geral' },
  { aba: 'auditoria', rotulo: 'Auditoria' },
  { aba: 'seguranca', rotulo: 'Segurança' },
  { aba: 'dados', rotulo: 'Dados e backup' },
  { aba: 'sobre', rotulo: 'Sobre' },
];
const abaDe = (v: string | null): Aba =>
  v === 'auditoria' || v === 'seguranca' || v === 'dados' || v === 'sobre' ? v : 'visao';

type Filtros = {
  readonly pessoa: string | null;
  readonly area: string | null;
  readonly dia: string | null;
  readonly pagina: number;
};

function endereco(p: { aba: Aba; f?: Filtros | undefined; evento?: string | null }) {
  const q = new URLSearchParams();
  if (p.aba !== 'visao') q.set('aba', p.aba);
  if (p.f?.pessoa) q.set('pessoa', p.f.pessoa);
  if (p.f?.area) q.set('area', p.f.area);
  if (p.f?.dia) q.set('dia', p.f.dia);
  if (p.f && p.f.pagina > 1) q.set('pagina', String(p.f.pagina));
  if (p.evento) q.set('evento', p.evento);
  const s = q.toString();
  return `#/auditoria${s ? `?${s}` : ''}`;
}
const ir = (href: string) => {
  window.location.hash = href.slice(1);
};

const maiuscula = (s: string) => (s ? (s[0]?.toUpperCase() ?? '') + s.slice(1) : s);

export function SaudeEAuditoria({ parametros }: { eu: Eu | null; parametros: URLSearchParams }) {
  const aba = abaDe(parametros.get('aba'));
  const evento = parametros.get('evento');
  const pagina = Math.max(1, Number(parametros.get('pagina') ?? 1) || 1);
  const f: Filtros = {
    pessoa: parametros.get('pessoa'),
    area: parametros.get('area'),
    dia: parametros.get('dia'),
    pagina,
  };
  const saude = useLeitura(lerSaude, 'saude');
  const versao = useVersao();

  if (saude.falha === 'sem-acesso') return <SemAcesso area="Saúde e auditoria" />;

  const atual: Aba = evento ? 'auditoria' : aba;
  let corpo: ReactNode;
  if (atual === 'visao')
    corpo = (
      <div className="sd-grade">
        <div className="sd-coluna">
          <Servicos saude={saude.d} falhou={saude.falha !== null} aoTentar={saude.recarregar} />
          {saude.d && <Incidentes saude={saude.d} />}
        </div>
        <Trilha f={f} aba="visao" />
      </div>
    );
  else if (atual === 'auditoria')
    corpo = (
      <div className="sd-grade sd-grade-auditoria">
        <Trilha f={f} aba="auditoria" selecionado={evento} />
        {evento ? (
          <DetalheDoEvento key={evento} id={evento} voltar={endereco({ aba: 'auditoria', f })} />
        ) : (
          <div className="sd-cartao sd-evento-vazio">
            <EstadoVazio
              icone="file"
              titulo="Escolha um evento"
              texto="O detalhe mostra quem, quando, o que mudou e os metadados já redigidos pelo servidor. Nada aqui pode ser editado."
            />
          </div>
        )}
      </div>
    );
  else
    corpo = !saude.d ? (
      saude.falha ? (
        <ErroDeArea oQue="a saúde do sistema" aoTentar={saude.recarregar} />
      ) : (
        <div className="sd-carregando" aria-busy="true" />
      )
    ) : atual === 'seguranca' ? (
      <div className="sd-cartoes">
        <Seguranca s={saude.d.seguranca} />
        <OndeAgir />
      </div>
    ) : atual === 'dados' ? (
      <div className="sd-cartoes">
        <Dados d={saude.d.dados} />
        <Servicos
          saude={saude.d}
          falhou={false}
          aoTentar={saude.recarregar}
          so={['Banco de dados do InovaPay', 'Upload de fotos', 'Exportações']}
          titulo="Serviços de dados"
        />
      </div>
    ) : (
      <div className="sd-cartoes">
        <Sobre v={versao.v} falhou={versao.falhou} verificadoEm={saude.d.verificadoEm} />
      </div>
    );

  return (
    <section className="sd">
      <nav className="sd-subnav" aria-label="Saúde e auditoria">
        {ABAS.map((a) => (
          <a
            key={a.aba}
            href={endereco({
              aba: a.aba,
              f: a.aba === 'visao' || a.aba === 'auditoria' ? f : undefined,
            })}
            className={atual === a.aba ? 'sd-sel' : ''}
            aria-current={atual === a.aba ? 'page' : undefined}
          >
            {a.rotulo}
          </a>
        ))}
        <span className="sd-subnav-espaco" />
        <span className="sd-subnav-direita">
          {saude.d
            ? `status retornados pelo servidor · ${hora(saude.d.verificadoEm)}`
            : saude.falha
              ? 'servidor sem resposta'
              : 'consultando o servidor…'}
        </span>
        <SeloDoAmbiente v={versao.v} falhou={versao.falhou} />
      </nav>
      <div className="sd-corpo">{corpo}</div>
    </section>
  );
}

/* ------------------------------ saúde dos serviços ------------------------------ */

const ROTULO_DO_SERVICO: Record<EstadoDoServico, string> = {
  saudavel: 'Saudável',
  degradado: 'Degradado',
  indisponivel: 'Indisponível',
  desconhecido: 'Desconhecido',
};

function EstadoDoServicoTag({ e }: { e: EstadoDoServico }) {
  return (
    <span className={`sd-estado sd-estado-${e}`}>
      {e === 'saudavel' && <Icone nome="check" tamanho={12} traco={2.2} />}
      {(e === 'degradado' || e === 'indisponivel') && (
        <Icone nome="warn" tamanho={12} traco={1.9} />
      )}
      {ROTULO_DO_SERVICO[e]}
    </span>
  );
}

function Servicos({
  saude,
  falhou,
  aoTentar,
  so,
  titulo = 'Saúde dos serviços',
}: {
  saude: SaudeDoSistema | null;
  falhou: boolean;
  aoTentar: () => void;
  so?: readonly string[];
  titulo?: string;
}) {
  const lista = saude ? saude.servicos.filter((s) => !so || so.includes(s.nome)) : [];
  return (
    <div className="sd-cartao sd-servicos">
      <div className="sd-cartao-topo">
        <h2>{titulo}</h2>
      </div>
      {!saude ? (
        falhou ? (
          <ErroDeArea oQue="a saúde dos serviços" aoTentar={aoTentar} />
        ) : (
          <div className="sd-carregando sd-carregando-curto" aria-busy="true" />
        )
      ) : (
        <ul className="sd-servicos-lista">
          {lista.map((s) => (
            <li key={s.nome} className={`sd-servico sd-servico-${s.estado}`}>
              <span className="sd-servico-texto">
                <span className="sd-servico-nome">{s.nome}</span>
                <span className="sd-servico-detalhe">{s.detalhe}</span>
              </span>
              <EstadoDoServicoTag e={s.estado} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** O que acontece na operação quando cada serviço falha. Descreve a consequência; não inventa dado. */
const IMPACTO: Record<string, string> = {
  'InovaPay API':
    'as telas não carregam nem gravam. Nada feito agora chega ao servidor até ele voltar.',
  'Banco de dados do InovaPay':
    'consultas e gravações ficam lentas ou falham. Toda gravação só vale depois de confirmada.',
  Autenticação: 'novas entradas podem falhar; quem já está dentro continua trabalhando.',
  'Upload de fotos':
    'fotos de auditoria podem demorar para enviar. As contagens continuam sendo salvas.',
  'Integração HOS':
    'Central de Vendas, conferência e custo mostram a última carga recebida. Os lançamentos do InovaPay continuam.',
  Exportações: 'relatórios e planilhas podem não ser gerados.',
};

function Incidentes({ saude }: { saude: SaudeDoSistema }) {
  const ruins = saude.servicos.filter(
    (s) => s.estado === 'degradado' || s.estado === 'indisponivel',
  );
  const desconhecidos = saude.servicos.filter((s) => s.estado === 'desconhecido').length;
  if (ruins.length === 0)
    return (
      <div className="sd-cartao sd-incidente sd-incidente-calmo">
        <div className="sd-incidente-topo">
          <Icone nome="check" tamanho={16} traco={2} />
          <div className="sd-incidente-titulo">Nenhum serviço degradado ou indisponível</div>
          <span className="sd-incidente-desde">verificado às {hora(saude.verificadoEm)}</span>
        </div>
        {desconhecidos > 0 && (
          <p className="sd-incidente-texto">
            {desconhecidos} serviço(s) aparecem como <b>Desconhecido</b>: o servidor ainda não tem
            como verificá-los, e o motivo está na linha de cada um. Desconhecido não é saudável.
          </p>
        )}
      </div>
    );
  return (
    <>
      {ruins.map((s) => (
        <div key={s.nome} className={`sd-cartao sd-incidente sd-incidente-${s.estado}`}>
          <div className="sd-incidente-topo">
            <Icone nome="warn" tamanho={16} traco={1.7} />
            <div className="sd-incidente-titulo">
              {s.nome} {s.estado === 'degradado' ? 'degradado' : 'indisponível'}
            </div>
            <span className="sd-incidente-desde">verificado às {hora(saude.verificadoEm)}</span>
          </div>
          <p className="sd-incidente-texto">
            <b>Impacto:</b> {IMPACTO[s.nome] ?? 'o servidor não descreveu o impacto deste serviço.'}
          </p>
          <p className="sd-incidente-nota">
            Detalhe do servidor: {s.detalhe} · o InovaPay ainda não abre incidente numerado nem
            envia notificação automática.
          </p>
        </div>
      ))}
    </>
  );
}

/* ------------------------------ trilha de auditoria ------------------------------ */

function IconeDoAtor({ t }: { t: EventoDaTrilha['atorTipo'] }) {
  if (t === 'usuario')
    return (
      <span className="sd-ator-icone sd-ator-usuario">
        <Icone nome="user" tamanho={12} traco={1.8} />
      </span>
    );
  if (t === 'sistema')
    return (
      <span className="sd-ator-icone sd-ator-sistema">
        <Icone nome="cog" tamanho={12} traco={1.6} />
      </span>
    );
  return (
    <span className="sd-ator-icone sd-ator-integracao">
      <IconeLocal nome="plug" tamanho={12} />
    </span>
  );
}

/** Linha da trilha: só a hora quando é de hoje (ou o dia já está no filtro); senão, dia e hora. */
const horaDaLinha = (iso: string, filtrouDia: boolean) =>
  filtrouDia || diaISO(iso) === hojeISO() ? hora(iso) : diaHora(iso);

function Trilha({ f, aba, selecionado }: { f: Filtros; aba: Aba; selecionado?: string | null }) {
  const t = useLeitura(() => lerTrilha(f), JSON.stringify(f));
  const mudar = (x: Partial<Filtros>) =>
    ir(endereco({ aba: aba === 'visao' ? 'visao' : 'auditoria', f: { ...f, pagina: 1, ...x } }));
  const d = t.d;
  const ini = d && d.eventos.length ? (d.pagina - 1) * 50 + 1 : 0;
  const fim = d ? ini + d.eventos.length - (d.eventos.length ? 1 : 0) : 0;
  const temProxima = d ? fim < d.total : false;

  return (
    <div className="sd-cartao sd-trilha">
      <div className="sd-cartao-topo sd-trilha-topo">
        <h2>Auditoria global</h2>
        <label className="sd-filtro">
          <span>Pessoa</span>
          <select
            value={f.pessoa ?? ''}
            onChange={(e) => mudar({ pessoa: e.target.value || null })}
          >
            <option value="">Todas</option>
            {d?.pessoas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </select>
          <Icone nome="chev-d" tamanho={12} traco={1.6} />
        </label>
        <label className="sd-filtro">
          <span>Área</span>
          <select value={f.area ?? ''} onChange={(e) => mudar({ area: e.target.value || null })}>
            <option value="">Todas</option>
            {d?.areas.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <Icone nome="chev-d" tamanho={12} traco={1.6} />
        </label>
        <label className="sd-filtro sd-filtro-dia">
          <span className="sd-sr">Dia</span>
          <input
            type="date"
            value={f.dia ?? ''}
            max={hojeISO()}
            onChange={(e) => mudar({ dia: e.target.value || null })}
          />
        </label>
        {(f.pessoa || f.area || f.dia) && (
          <button
            type="button"
            className="sd-limpar"
            onClick={() => mudar({ pessoa: null, area: null, dia: null })}
          >
            Limpar
          </button>
        )}
      </div>
      <div className="sd-trilha-rolo">
        {!d ? (
          t.falha ? (
            <ErroDeArea oQue="a trilha de auditoria" aoTentar={t.recarregar} />
          ) : (
            <div className="sd-carregando sd-carregando-curto" aria-busy="true" />
          )
        ) : d.eventos.length === 0 ? (
          <EstadoVazio
            icone="file"
            titulo={
              f.pessoa || f.area || f.dia ? 'Nenhum evento com estes filtros' : 'Trilha vazia'
            }
            texto={
              f.pessoa || f.area || f.dia
                ? 'Tente outro dia, outra pessoa ou outra área.'
                : 'Nenhum evento registrado ainda. Cada ação que muda algo no sistema entra aqui.'
            }
          />
        ) : (
          <table className="sd-tabela">
            <thead>
              <tr>
                <th>Hora</th>
                <th>Ator</th>
                <th>Área</th>
                <th>Ação</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {d.eventos.map((e) => {
                const href = endereco({ aba: 'auditoria', f, evento: e.id });
                return (
                  <tr
                    key={e.id}
                    className={selecionado === e.id ? 'sd-linha-sel' : ''}
                    onClick={() => ir(href)}
                  >
                    <td className="sd-hora">{horaDaLinha(e.em, Boolean(f.dia))}</td>
                    <td>
                      <span className="sd-ator">
                        <IconeDoAtor t={e.atorTipo} />
                        {e.ator}
                      </span>
                    </td>
                    <td className="sd-area">{e.area}</td>
                    <td>
                      <a href={href} className="sd-acao" onClick={(x) => x.stopPropagation()}>
                        {e.acao}
                      </a>
                    </td>
                    <td className={e.resultado === 'Negado' ? 'sd-res-negado' : 'sd-res-ok'}>
                      {e.resultado}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="sd-rodape">
        <span>
          {d
            ? d.total === 0
              ? '0 eventos'
              : `${String(ini)}–${String(fim)} de ${String(d.total)} eventos`
            : '—'}
        </span>
        {d && (d.pagina > 1 || temProxima) && (
          <span className="sd-paginas">
            <button
              type="button"
              disabled={d.pagina <= 1}
              onClick={() => ir(endereco({ aba, f: { ...f, pagina: d.pagina - 1 } }))}
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={!temProxima}
              onClick={() => ir(endereco({ aba, f: { ...f, pagina: d.pagina + 1 } }))}
            >
              Próxima
            </button>
          </span>
        )}
        <span className="sd-rodape-nota">Somente consulta · ninguém edita ou apaga a trilha</span>
      </div>
    </div>
  );
}

const valor = (v: unknown) =>
  v === null || v === undefined
    ? '—'
    : typeof v === 'string'
      ? v
      : typeof v === 'number' || typeof v === 'boolean'
        ? String(v)
        : JSON.stringify(v);

function DetalheDoEvento({ id, voltar }: { id: string; voltar: string }) {
  const ev = useLeitura(() => lerEventoDaTrilha(id), id);
  const e = ev.d;
  const meta = e ? Object.entries(e.metadados) : [];
  const temAntesDepois = e ? 'antes' in e.metadados || 'depois' in e.metadados : false;
  return (
    <div className="sd-cartao sd-evento">
      <div className="sd-cartao-topo sd-evento-topo">
        <div className="sd-evento-titulo">
          <h2>{e ? `Evento · ${e.acao}` : 'Evento'}</h2>
          {e && (
            <span>
              {e.ator} · {diaHora(e.em)} · {e.area}
            </span>
          )}
        </div>
        <a className="sd-fechar" href={voltar} aria-label="Fechar o detalhe">
          <Icone nome="x" tamanho={16} traco={1.7} />
        </a>
      </div>
      {!e ? (
        ev.falha ? (
          <ErroDeArea oQue="o evento" aoTentar={ev.recarregar} />
        ) : (
          <div className="sd-carregando sd-carregando-curto" aria-busy="true" />
        )
      ) : (
        <div className="sd-evento-corpo">
          {temAntesDepois && (
            <div className="sd-antes-depois">
              <span className="sd-ad-rotulo">
                {e.entidade} · {e.entidadeId}
              </span>
              <span className="sd-ad-antes">{valor(e.metadados['antes'])}</span>
              <Icone nome="chev-r" tamanho={13} traco={1.8} />
              <span className="sd-ad-depois">{valor(e.metadados['depois'])}</span>
            </div>
          )}
          <dl className="sd-pares">
            <div>
              <dt>Resultado</dt>
              <dd className={e.resultado === 'Negado' ? 'sd-res-negado' : 'sd-res-ok'}>
                {e.resultado}
              </dd>
            </div>
            <div>
              <dt>Ator</dt>
              <dd>
                {e.ator} ·{' '}
                {e.atorTipo === 'usuario'
                  ? 'pessoa'
                  : e.atorTipo === 'sistema'
                    ? 'sistema'
                    : e.atorTipo === 'importacao'
                      ? 'importação'
                      : 'integração'}
              </dd>
            </div>
            <div>
              <dt>Tipo</dt>
              <dd className="sd-mono">{e.tipo}</dd>
            </div>
            <div>
              <dt>Objeto</dt>
              <dd>
                {e.entidade} · <span className="sd-mono">{e.entidadeId}</span>
              </dd>
            </div>
            {e.requestId && (
              <div>
                <dt>Requisição</dt>
                <dd className="sd-mono">{e.requestId}</dd>
              </div>
            )}
          </dl>
          {meta.filter(([k]) => k !== 'antes' && k !== 'depois').length > 0 && (
            <>
              <div className="sd-rotulo-mono">METADADOS</div>
              <dl className="sd-pares sd-pares-meta">
                {meta
                  .filter(([k]) => k !== 'antes' && k !== 'depois')
                  .map(([k, v]) => (
                    <div key={k}>
                      <dt className="sd-mono">{k}</dt>
                      <dd>{valor(v)}</dd>
                    </div>
                  ))}
              </dl>
            </>
          )}
          <p className="sd-nota">
            Registro imutável: ninguém edita ou apaga a trilha. Os metadados chegam já redigidos
            pelo servidor — nunca senha, token ou segredo.
          </p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ segurança, dados, sobre ------------------------------ */

function Par({
  rotulo,
  children,
  tom,
}: {
  rotulo: string;
  children: ReactNode;
  tom?: string | undefined;
}) {
  return (
    <div className="sd-par">
      <span>{rotulo}</span>
      <b className={tom ? `sd-tom-${tom}` : undefined}>{children}</b>
    </div>
  );
}

function Seguranca({ s }: { s: SaudeDoSistema['seguranca'] }) {
  return (
    <div className="sd-cartao">
      <div className="sd-cartao-topo sd-topo-icone">
        <Icone nome="shield" tamanho={17} traco={1.6} />
        <h2>Segurança operacional</h2>
      </div>
      <div className="sd-cartao-corpo">
        <Par rotulo="Segundo fator" tom="neutro">
          {maiuscula(s.segundoFator)}
        </Par>
        <Par rotulo="Sessões ativas">{s.sessoesAtivas}</Par>
        <Par
          rotulo="Bloqueios de qualidade (premiação)"
          tom={s.bloqueiosOperacionais > 0 ? 'erro' : undefined}
        >
          <a href="#/qualidade?aba=bloqueios">
            {s.bloqueiosOperacionais > 0 ? `${String(s.bloqueiosOperacionais)} · premiação` : '0'}
          </a>
        </Par>
        <Par rotulo="Contas bloqueadas (acesso)" tom={s.contasBloqueadas > 0 ? 'aviso' : undefined}>
          <a href="#/equipe">{s.contasBloqueadas}</a>
        </Par>
        <Par rotulo="Acessos negados · 24 h">{s.negadas24h}</Par>
        <p className="sd-nota">
          Bloqueio de qualidade (Qualidade do Giro) e conta bloqueada (Equipe) são coisas
          diferentes, cada uma com seu link. Nenhuma senha, PIN ou token é exibido.
        </p>
      </div>
    </div>
  );
}

function OndeAgir() {
  const itens: readonly { href: string; titulo: string; texto: string }[] = [
    {
      href: '#/qualidade?aba=bloqueios',
      titulo: 'Bloqueios de qualidade',
      texto: 'retêm só a premiação; o Giro continua',
    },
    { href: '#/equipe', titulo: 'Equipe e contas', texto: 'suspender, reativar e aprovar acesso' },
    { href: '#/perfis', titulo: 'Perfis e permissões', texto: 'o que cada perfil pode fazer' },
  ];
  return (
    <div className="sd-cartao">
      <div className="sd-cartao-topo sd-topo-icone">
        <Icone nome="lock" tamanho={17} traco={1.6} />
        <h2>Onde agir</h2>
      </div>
      <ul className="sd-links">
        {itens.map((i) => (
          <li key={i.href}>
            <a href={i.href}>
              <span>
                <b>{i.titulo}</b>
                <span>{i.texto}</span>
              </span>
              <Icone nome="chev-r" tamanho={15} traco={1.7} />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Dados({ d }: { d: SaudeDoSistema['dados'] }) {
  return (
    <div className="sd-cartao">
      <div className="sd-cartao-topo sd-topo-icone">
        <Icone nome="db" tamanho={17} traco={1.6} />
        <h2>Dados e backup</h2>
      </div>
      <div className="sd-cartao-corpo">
        <Par rotulo="Backup de dados do InovaPay" tom="neutro">
          Estado desconhecido
        </Par>
        <p className="sd-par-nota">{maiuscula(d.backup)}.</p>
        <Par rotulo="Retenção de fotos e trilha">{maiuscula(d.retencao)}</Par>
        <Par rotulo="Tamanho do banco" tom={d.banco.tamanho ? undefined : 'neutro'}>
          {d.banco.tamanho ?? 'desconhecido'}
        </Par>
        <Par rotulo="Migrações aplicadas">{d.banco.migracoes}</Par>
        <p className="sd-nota">
          Sem download nem “restaurar” nesta tela: restauração é ação crítica com fluxo próprio.
        </p>
      </div>
    </div>
  );
}

function Sobre({
  v,
  falhou,
  verificadoEm,
}: {
  v: Versao | null;
  falhou: boolean;
  verificadoEm: string;
}) {
  const [copia, setCopia] = useState<'ok' | 'falhou' | null>(null);
  const amb = nomeDoAmbiente(v?.ambiente ?? null);
  const copiar = () => {
    const texto = [
      `Produto: InovaPay`,
      `Versão: ${v?.versao ?? 'não informada pelo build'}`,
      `Commit: ${v?.commit ?? 'não informado pelo build'}`,
      `Ambiente: ${amb.rotulo}`,
      `Status verificado em: ${diaHora(verificadoEm)}`,
    ].join('\n');
    void navigator.clipboard
      .writeText(texto)
      .then(() => setCopia('ok'))
      .catch(() => setCopia('falhou'));
  };
  return (
    <div className="sd-cartao">
      <div className="sd-cartao-topo sd-topo-icone">
        <IconeLocal nome="help" tamanho={17} />
        <h2>Suporte e sobre</h2>
      </div>
      <div className="sd-cartao-corpo">
        {falhou && !v && (
          <p className="sd-par-nota">
            O servidor não respondeu a versão. Os campos ficam em branco.
          </p>
        )}
        <Par rotulo="Produto">InovaPay</Par>
        <Par rotulo="Versão">
          <span className="sd-mono">{v?.versao ?? 'não informada pelo build'}</span>
        </Par>
        <Par rotulo="Commit" tom={v?.commit ? undefined : 'neutro'}>
          <span className="sd-mono">{v?.commit ?? 'não informado pelo build'}</span>
        </Par>
        <Par rotulo="Ambiente">
          <span className={`cg-amb cg-amb-${amb.tom} sd-amb-normal`}>{amb.rotulo}</span>
        </Par>
        {v?.fuso && <Par rotulo="Fuso operacional">{v.fuso}</Par>}
        <button type="button" className="sd-copiar" onClick={copiar} disabled={!v}>
          <IconeLocal nome="copy" tamanho={14} traco={1.7} />
          Copiar informações para o suporte
        </button>
        {copia && (
          <p className={`sd-copia sd-copia-${copia}`} role="status">
            {copia === 'ok'
              ? 'Copiado: versão, commit, ambiente e hora da verificação.'
              : 'O navegador não deixou copiar. Anote a versão e o ambiente acima.'}
          </p>
        )}
        <p className="sd-nota">Copia versão, commit e ambiente. Nunca credenciais.</p>
      </div>
    </div>
  );
}
